import { useState, useCallback } from 'react';
import { UsersDetail } from '../../../types/settings/UsersTypes';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import ModernUserForm from '../../../components/Forms/ModernUserForm';
import { getUsers, getUserData } from '@/actions/data-fetch-users';
import { toastMessage } from '@/utils/toast-message';
import ModernUsersTable from './ModernUsersTable';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { PaginationProps, UsersApiData } from '../../../types/settings/settings';
import { Users, Trash2, Search } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';

export function UsersClientWrapper({
    initialData,
    pagination
}: UsersApiData) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UsersDetail | null>(null);
    const [data, setData] = useState<UsersDetail[]>(initialData || []);
    const [currentPagination, setCurrentPagination] = useState<PaginationProps | undefined>(pagination);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [isPurgingCache, setIsPurgingCache] = useState(false);

    const fetchData = useCallback(async (params: {
        page: number,
        search_term?: string,
        sort_column?: string,
        sort_order?: 'ASC' | 'DESC',
    }) => {
        try {
            setIsLoading(true);
            const response = await getUsers({ ...params });
            if (response.data) {
                setData(response.data.users || []);
                setCurrentPagination(response.data.pagination);
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, "error");
            } else {
                toastMessage("An unknown error occurred", "error");
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleOpenModal = async (user?: UsersDetail) => {
        if (user?.uuid) {
            // Fetch fresh user data by UUID when editing
            setIsModalOpen(true);
            setIsLoadingUserDetails(true);
            try {
                const response = await getUserData(user.uuid);
                if (response.status === 'success' && response.data) {
                    setSelectedUser(response.data as unknown as UsersDetail);
                } else {
                    // Fallback to list data if fetch fails
                    setSelectedUser(user);
                    toastMessage('Could not fetch latest user data', 'warning');
                }
            } catch (error) {
                // Fallback to list data if fetch fails
                setSelectedUser(user);
                toastMessage('Could not fetch latest user data', 'warning');
            } finally {
                setIsLoadingUserDetails(false);
            }
        } else {
            // New user - no fetch needed
            setSelectedUser(null);
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleDelete = (uuid: string) => {
        setData(prev => prev.filter(u => u.uuid !== uuid));
    };

    const handleSuccess = (updatedUser: UsersDetail) => {
        setData(prevData => {
            const userIndex = prevData.findIndex(u => u.uuid === updatedUser.uuid);
            if (userIndex !== -1) {
                const newData = [...prevData];
                newData[userIndex] = updatedUser;
                return newData;
            } else {
                return [updatedUser, ...prevData];
            }
        });

        // Show success message
        toastMessage(
            selectedUser ? 'User updated successfully' : 'User created successfully',
            'success'
        );

        // Close modal
        handleCloseModal();
    };

    const handlePageChange = async (page: number) => {
        await fetchData({
            page,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder,
        });
    };

    const handleSearch = async (term: string): Promise<void> => {
        setSearchTerm(term);
        await fetchData({
            page: 1,
            search_term: term,
            sort_column: sortColumn,
            sort_order: sortOrder,
        });
    };

    const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
        setSortColumn(column);
        setSortOrder(order);
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: column,
            sort_order: order,
        });
    };

    const handleUpdateUser = (updatedUser: UsersDetail) => {
        setData(prevData =>
            prevData.map(u => u.uuid === updatedUser.uuid ? updatedUser : u)
        );
    };

    const handlePurgeCache = async () => {
        try {
            setIsPurgingCache(true);
            await apiRequest('/api/users/cache/purge', {
                method: 'DELETE'
            });
            toastMessage('Users cache purged successfully', 'success');
            // Refresh the data after purging cache
            await fetchData({
                page: 1,
                search_term: searchTerm,
                sort_column: sortColumn,
                sort_order: sortOrder,
            });
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, 'error');
            } else {
                toastMessage('Failed to purge cache', 'error');
            }
        } finally {
            setIsPurgingCache(false);
        }
    };

    const searchSlot = (
        <form
            onSubmit={(e) => { e.preventDefault(); handleSearch(searchInput); }}
            className="flex items-center"
        >
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                    type="text"
                    placeholder="Search users..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent dark:bg-gray-700 dark:text-white w-52"
                />
            </div>
        </form>
    );

    return (
        <div className="h-full flex flex-col">
            <div className="sticky top-0 z-10">
                <ModernSettingHeader
                    title="Team Members"
                    description="Manage team members and their roles."
                    buttonText="Add New User"
                    onClick={() => handleOpenModal()}
                    icon={<Users className="w-6 h-6" />}
                    actionSlot={searchSlot}
                    secondaryButton={{
                        text: 'Purge Cache',
                        onClick: handlePurgeCache,
                        icon: <Trash2 className="w-4 h-4" />,
                        variant: 'warning',
                        loading: isPurgingCache
                    }}
                />
            </div>
            <div className="flex-1 overflow-hidden p-6">
                <ModernUsersTable
                    initialData={data}
                    onEdit={handleOpenModal}
                    onUpdateUser={handleUpdateUser}
                    pagination={currentPagination}
                    onPageChange={handlePageChange}
                    onSort={handleSort}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                />
            </div>

            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={selectedUser ? 'Edit User' : 'Add New User'}
                subtitle={selectedUser ? 'Update user details and permissions' : 'Create a new user account and set their permissions'}
                icon={<Users className="w-6 h-6" />}
            >
                {isLoadingUserDetails ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center space-y-3">
                            <div className="w-8 h-8 border-2 border-brandColor border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Loading user details...</p>
                        </div>
                    </div>
                ) : (
                    <ModernUserForm
                        onClose={handleCloseModal}
                        uuid={selectedUser?.uuid}
                        initialData={selectedUser}
                        onSuccess={handleSuccess}
                        onDelete={handleDelete}
                    />
                )}
            </SettingsSlideModal>
        </div>
    );
}